import type { Vec2 } from "./types";
import { distance } from "./geom";

/**
 * Wall-mount geometry helpers.
 *
 * Wall mounts live in a per-wall 2D plane defined by:
 *   - alongWall: distance from the wall's start vertex (inches), 0 → wallLength.
 *   - fromFloor: vertical distance from the floor to the mount's center.
 *
 * The mount's footprint on the wall is an axis-aligned rectangle:
 *   alongWall ∈ [center - width/2, center + width/2]
 *   fromFloor ∈ [center - height/2, center + height/2]
 *
 * Openings (doors / windows) share the same coord system on their wall, so
 * mount-vs-opening overlap is a simple AABB intersect on (alongWall, fromFloor).
 *
 * The validator at functions/src/wallGeom.ts is a verbatim mirror — keep them
 * in sync when changing the geometric contract.
 */

/** Length (inches) of polygon edge `wallIndex`, going from `points[i]` to `points[(i+1) % n]`. */
export function wallLength(points: Vec2[], wallIndex: number): number {
  const n = points.length;
  if (n < 2) return 0;
  const a = points[wallIndex % n];
  const b = points[(wallIndex + 1) % n];
  if (!a || !b) return 0;
  return distance(a, b);
}

/** True iff a (centered) mount rectangle stays strictly within the wall's
 *  extents, both horizontally (along the wall) and vertically (floor → ceiling).
 *  Returns false on any non-finite input so a buggy AI response can't sneak past. */
export function mountFitsOnWall(
  alongWall: number,
  fromFloor: number,
  width: number,
  height: number,
  wallLen: number,
  ceilingHeight: number
): boolean {
  if (
    !Number.isFinite(alongWall) ||
    !Number.isFinite(fromFloor) ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    !Number.isFinite(wallLen) ||
    !Number.isFinite(ceilingHeight)
  ) {
    return false;
  }
  if (width <= 0 || height <= 0 || wallLen <= 0 || ceilingHeight <= 0) return false;
  const halfW = width / 2;
  const halfH = height / 2;
  if (alongWall - halfW < 0) return false;
  if (alongWall + halfW > wallLen) return false;
  if (fromFloor - halfH < 0) return false;
  if (fromFloor + halfH > ceilingHeight) return false;
  return true;
}

export interface WallRect {
  alongWall: number;
  fromFloor: number;
  width: number;
  height: number;
}

/** Strict-inequality AABB overlap on the wall plane. Touching edges
 *  (e.g. a mount whose right side coincides exactly with a window's
 *  left side) is treated as not overlapping. */
export function wallRectsOverlap(a: WallRect, b: WallRect): boolean {
  const aMinX = a.alongWall - a.width / 2;
  const aMaxX = a.alongWall + a.width / 2;
  const aMinY = a.fromFloor - a.height / 2;
  const aMaxY = a.fromFloor + a.height / 2;
  const bMinX = b.alongWall - b.width / 2;
  const bMaxX = b.alongWall + b.width / 2;
  const bMinY = b.fromFloor - b.height / 2;
  const bMaxY = b.fromFloor + b.height / 2;
  if (aMaxX <= bMinX) return false;
  if (bMaxX <= aMinX) return false;
  if (aMaxY <= bMinY) return false;
  if (bMaxY <= aMinY) return false;
  return true;
}

/** Convenience alias for "this mount overlaps an opening on its wall". The
 *  geometry is identical to mount-vs-mount; the separate name keeps call
 *  sites self-documenting. */
export const mountOverlapsOpening = wallRectsOverlap;

/** Convenience alias for "this mount overlaps another mount on its wall". */
export const mountOverlapsMount = wallRectsOverlap;
